// Local Storage Manager with Error Handling

import { STORAGE_KEYS } from './config.js';

class StorageManager {
  constructor() {
    this.available = this.checkAvailability();
  }

  checkAvailability() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      console.error('LocalStorage not available:', e);
      return false;
    }
  }

  setItem(key, value) {
    if (!this.available) {
      console.warn('Storage not available');
      return false;
    }

    try {
      localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
      return true;
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        this.handleQuotaExceeded();
      }
      console.error('Error saving to localStorage:', e);
      return false;
    }
  }

  getItem(key, defaultValue = null) {
    if (!this.available) return defaultValue;

    try {
      const value = localStorage.getItem(key);
      if (value === null) return defaultValue;

      // Try to parse as JSON, fallback to string
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    } catch (e) {
      console.error('Error reading from localStorage:', e);
      return defaultValue;
    }
  }

  removeItem(key) {
    if (!this.available) return false;

    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.error('Error removing from localStorage:', e);
      return false;
    }
  }

  clear(prefix = '') {
    if (!this.available) return 0;

    try {
      const keysToDelete = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!prefix || key.startsWith(prefix)) {
          keysToDelete.push(key);
        }
      }

      keysToDelete.forEach(key => localStorage.removeItem(key));
      return keysToDelete.length;
    } catch (e) {
      console.error('Error clearing localStorage:', e);
      return 0;
    }
  }

  getAllKeys(prefix = '') {
    if (!this.available) return [];

    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!prefix || key.startsWith(prefix)) {
          keys.push(key);
        }
      }
      return keys;
    } catch (e) {
      console.error('Error getting keys from localStorage:', e);
      return [];
    }
  }

  handleQuotaExceeded() {
    const message =
      'Spazio di archiviazione pieno! Esporta i tuoi dati e cancella le note vecchie.';
    alert(message);

    // Try to clear old data
    const oldKeys = this.getAllKeys('workout-note-');
    if (oldKeys.length > 100) {
      // Remove oldest 20%
      const toRemove = oldKeys.slice(0, Math.floor(oldKeys.length * 0.2));
      toRemove.forEach(key => this.removeItem(key));
      console.log(`Removed ${toRemove.length} old entries`);
    }
  }

  // Helper methods for workout-specific storage
  saveNote(week, day, exerciseIndex, note) {
    const key = `workout-${STORAGE_KEYS.NOTE}-${week}-${day}-${exerciseIndex}`;
    return this.setItem(key, note);
  }

  loadNote(week, day, exerciseIndex) {
    const key = `workout-${STORAGE_KEYS.NOTE}-${week}-${day}-${exerciseIndex}`;
    return this.getItem(key, '');
  }

  saveCompletion(week, day, exerciseIndex, completed = true) {
    const key = `workout-${STORAGE_KEYS.COMPLETION}-${week}-${day}-${exerciseIndex}`;
    if (completed) {
      const today = new Date().toISOString().split('T')[0];
      this.setItem(key, 'true');
      this.setItem(`${key}-date`, today);
      return true;
    } else {
      this.removeItem(key);
      this.removeItem(`${key}-date`);
      return true;
    }
  }

  isCompleted(week, day, exerciseIndex) {
    const key = `workout-${STORAGE_KEYS.COMPLETION}-${week}-${day}-${exerciseIndex}`;
    // getItem returns boolean true (from JSON.parse), not string 'true'
    return this.getItem(key) === true;
  }

  saveWorkoutCompletion(week, day) {
    const key = `workout-${STORAGE_KEYS.COMPLETED}-${week}-${day}`;
    const today = new Date().toISOString().split('T')[0];
    return this.setItem(key, today);
  }

  exportData() {
    const data = {};
    const keys = this.getAllKeys('workout-');
    keys.forEach(key => {
      data[key] = this.getItem(key);
    });
    return data;
  }

  importData(data) {
    let count = 0;
    for (const [key, value] of Object.entries(data)) {
      if (key.startsWith('workout-')) {
        if (this.setItem(key, value)) {
          count++;
        }
      }
    }
    return count;
  }
}

export const storage = new StorageManager();
